/* eslint-disable max-statements, multiline-comment-style, no-plusplus, no-unused-vars, no-warning-comments, no-console, quote-props, sort-keys */
/* eslint-disable react/jsx-no-bind, no-constant-condition */
import { Button, Col, Offcanvas, Row, Spinner } from "react-bootstrap";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";

import DownloadReportButton from "../../pages/practitioner/components/DownloadReportButton";
import Helmet from "react-helmet";
import LiaErrorBoundary from "../../components/LiaErrorBoundary";
import ReportHeader from "./ReportHeader";
import ReportNav from "./ReportNav";
import ReportReviewNav from "./ReportReviewNav";
import profileStatusMutation from "../profile/profileStatusMutation";
import { useUser } from "../UserContext";

/* 
 * List of report section "slugs" - 
 * these should match url segments after /report/ 
 * and be in the correct order
 */
const reportSections = [
	"introduction",
	"emotions",
	"positive-negative",
	"self-other",
	"thoughts-wants-feelings",
	"empathy-accuracy",
	"empathy-compassion",
	"relationship-strategies",
	"summary",
	"complete"
];

/*
 * Title text for each header section
 */
const reportSectionHeaders = {
	"introduction": "Welcome to Your EQQO Profile Report",
	"emotions": "Your Emotions",
	"positive-negative": "Positive / Negative Orientation",
	"self-other": "Self-Other Orientation",
	"thoughts-wants-feelings": "Thoughts, Wants, Feelings",
	"empathy-accuracy": "Empathy Accuracy",
	"empathy-compassion": "Empathy Compassion",
	"relationship-strategies": "Relationship Strategies",
	"summary": "EQQO Report Summary",
	"complete": "Report Complete"
}

let furthestSectionReviewed = 0;

const Report = () => {
	const { user, respondent } = useUser();
	const navigate = useNavigate();
	const location = useLocation();

	const [show, setShow] = useState(false);
	const handleClose = () => setShow(false);
	const handleShow = () => setShow(true);
	// Report routes require auth
	useEffect(() => {
		if (!user) {
			navigate("/sign-in");
		}
		// TODO: verify respondent profile status is "review" or "completed"
	}, [user]);

	const sectionIndex = useMemo(() => reportSections.indexOf(location.pathname.replace('/report/', '')), [location]);
	const nextSection = useMemo(() => sectionIndex < reportSections.length - 1 ? reportSections[sectionIndex + 1] : null, [sectionIndex]);
	const prevSection = useMemo(() => sectionIndex > 0 ? reportSections[sectionIndex - 1] : null, [sectionIndex]);
	furthestSectionReviewed = useMemo(() => sectionIndex + 1 > furthestSectionReviewed ? sectionIndex + 1 : furthestSectionReviewed, [sectionIndex]);
	const headerText = useMemo(() => {
		const page = location.pathname.replace('/report/', '');
		return reportSectionHeaders[page] || null;
	}, [location]);

	const queryClient = useQueryClient();
	const {
		isLoading,
		mutate
	} = profileStatusMutation(useMutation, {
		onError: (error) => {
			console.log(error);
		},
		onSuccess: () => {
			queryClient.invalidateQueries(["respondent", user.respondentId]);
			console.log('updated profile status');
			navigate('/profile/dashboard');
		}
	});
	
	useEffect(() => {
		// Close nav on location change
		handleClose();
	}, [location]);

	const completeReview = useCallback(() => {
		const mutationData = {
			id: user.weqProfileId,
			weqProfileAcfFields: {
				profilestatus: "completed"
			}
		};
		mutate(mutationData);
	}, [user]);

	return (
		<Row as="main" className="profile | bg-eqqo-blue-white">
			<Helmet>
				<title>{"Your EQQO Profile Report"}</title>
			</Helmet>

			<Col className="profile__sidebar bg-primary" lg={3}>
				<Offcanvas className="h-100" onHide={handleClose} responsive={'xl'} show={show}>
					{ respondent?.weqProfile.profilestatus === "review" && false ? 
						<ReportReviewNav activeSection={sectionIndex + 1} furthestSection={furthestSectionReviewed} handleClose={handleClose} />
						:
						<ReportNav activeSection={sectionIndex + 1} handleClose={handleClose} /> }
				</Offcanvas>
			</Col>
			<Col className="profile__content" lg={9}>
				<ReportHeader headerText={headerText} showMenu={handleShow} />
				<LiaErrorBoundary>
					<Outlet />
				</LiaErrorBoundary>
				{ respondent?.weqProfile.profilestatus === "review" && false ? 
					<div className={`d-flex gap-3 mt-5 ${ nextSection ? 'justify-content-between' : 'justify-content-center' } `}>
						{ prevSection && nextSection ? (
							<NavLink
								className="btn btn-eqqo-gray"
								to={`${prevSection}`}
							>
								<ChevronLeftIcon height={20} width={20} /> Go Back
							</NavLink>
						) : null }
						{ nextSection ? (
							<NavLink
								className="btn btn-primary"
								style={{ marginLeft: 'auto' }}
								to={`${nextSection}`}
							>
								Continue <ChevronRightIcon height={20} width={20} />
							</NavLink>
						) : <>
							<DownloadReportButton
								className="btn btn-eqqo-gray"
								name={`${user?.firstName} ${user?.lastName}`}
								pdfUrl={user?.weqProfilePdfUrl}
								profileId={user?.weqProfileId}
							>
								Download Your EQQO Profile Report (PDF)
							</DownloadReportButton>
							<Button className="btn btn-primary" onClick={completeReview}>
								Continue to Dashboard
								{
									isLoading
										? <Spinner animation="border" size="sm" />
										: <ChevronRightIcon height={20} width={20} />
								}
							</Button>
						</> }
					</div> : null }
			</Col>
		</Row>
	);
};

export default Report;
